import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import { StaffMessagesThread } from '../../components/StaffMessagesThread';
import { Badge } from '../../components/ui/badge';
import { MessageSquare } from 'lucide-react';
import { cn } from '../../lib/utils';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One conversation per sender: ADMIN TEAM + each department that wrote to you.
export default function EmployeeMessagesPage() {
  const [channels, setChannels] = useState([{ channel: 'admin', label: 'ADMIN TEAM', unread: 0 }]);
  const [selected, setSelected] = useState('admin');

  const loadChannels = useCallback(() => {
    axios.get(`${API}/users/me/message-channels`)
      .then((r) => {
        const ch = r.data?.channels || [];
        if (ch.length) setChannels(ch);
      })
      .catch(() => {});
  }, []);
  useEffect(loadChannels, [loadChannels]);
  useAutoRefresh(loadChannels, { match: ['/messages'], intervalMs: 20000 });

  const current = channels.find((c) => c.channel === selected) || channels[0];

  return (
    <EmployeeLayout>
      <div className="p-4 sm:p-6 space-y-6" data-testid="employee-messages-page">
        <div className="border-b border-border pb-5">
          <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-primary" strokeWidth={1.5} />
            MESSAGES
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Send your message to our team. Your message will be received by the relevant department, reviewed, and responded to within 48 hours.
          </p>
        </div>

        {channels.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="message-channels">
            {channels.map((c) => (
              <button
                key={c.channel}
                type="button"
                onClick={() => { setSelected(c.channel); setChannels((p) => p.map((x) => x.channel === c.channel ? { ...x, unread: 0 } : x)); }}
                className={cn(
                  'px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-wider font-semibold border transition-colors flex items-center gap-2',
                  selected === c.channel
                    ? 'bg-primary text-black border-primary'
                    : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent/50'
                )}
                data-testid={`message-channel-${c.channel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {c.label}
                {c.unread > 0 && (
                  <Badge className="bg-red-500 text-white rounded-full text-[10px] px-1.5 py-0">{c.unread}</Badge>
                )}
              </button>
            ))}
          </div>
        )}

        <StaffMessagesThread key={selected} viewer="employee" channel={selected} />
      </div>
    </EmployeeLayout>
  );
}
